/** Preview-only policy for the signature editor. Core repeats validation before storage and send. */
const COLORS = new Set([
  "black", "silver", "gray", "white", "maroon", "red", "purple", "fuchsia",
  "green", "lime", "olive", "yellow", "navy", "blue", "teal", "aqua", "transparent",
]);
const FONT_FAMILIES = new Set([
  "arial", "helvetica", "verdana", "georgia", "times new roman", "courier new",
  "sans-serif", "serif", "monospace",
]);

export function escapeSignatureHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function color(value: string): boolean {
  if (COLORS.has(value) || /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(value)) return true;
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(value);
  return Boolean(rgb && rgb.slice(1).every((part) => Number(part) <= 255));
}

function dimension(value: string, maximum: number, percent = false): boolean {
  if (value === "0") return true;
  const match = /^(\d+(?:\.\d{1,2})?)(px|pt|%)$/.exec(value);
  if (!match) return false;
  const amount = Number(match[1]);
  return match[2] === "%"
    ? percent && amount > 0 && amount <= 100
    : amount >= 0 && amount * (match[2] === "pt" ? 4 / 3 : 1) <= maximum;
}

function safeStyleValue(property: string, value: string): boolean {
  if (property === "color" || property === "background-color") return color(value);
  if (property === "font-family") {
    return value.split(",").every((family) =>
      FONT_FAMILIES.has(family.trim().replace(/^(['"])(.*)\1$/, "$2")));
  }
  if (property === "font-size") return dimension(value, 96) && value !== "0";
  if (property === "font-weight") return /^(normal|bold|[1-9]00)$/.test(value);
  if (property === "font-style") return /^(normal|italic)$/.test(value);
  if (property === "text-decoration") return /^(none|underline|line-through)$/.test(value);
  if (property === "text-align") return /^(left|center|right|justify)$/.test(value);
  if (property === "vertical-align") return /^(top|middle|bottom|baseline)$/.test(value);
  if (property === "width" || property === "max-width") return dimension(value, 1200, true);
  if (property === "line-height") {
    return /^(?:[1-2](?:\.\d{1,2})?|3(?:\.0{1,2})?)$/.test(value) || dimension(value, 128);
  }
  if (property === "padding") {
    const parts = value.split(/\s+/);
    return parts.length <= 4 && parts.every((part) => dimension(part, 64));
  }
  if (/^padding-(top|right|bottom|left)$/.test(property)) return dimension(value, 64);
  if (/^border(?:-(top|right|bottom|left))?$/.test(property)) {
    if (value === "0" || value === "none") return true;
    const border = /^(\S+) (solid|dashed|dotted) (.+)$/.exec(value);
    return Boolean(border && dimension(border[1]!, 8) && color(border[3]!));
  }
  if (property === "border-collapse") return /^(collapse|separate)$/.test(value);
  if (property === "table-layout") return /^(auto|fixed)$/.test(value);
  return false;
}

/** A closed value grammar: no URLs, escapes, functions other than rgb, CSS variables or selectors. */
export function signatureEditorStyle(source: string): string {
  const properties = new Map<string, string>();
  for (const declaration of source.split(";")) {
    const colon = declaration.indexOf(":");
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim().toLowerCase().replace(/\s+/g, " ");
    if (!safeStyleValue(property, value)) continue;
    // Preserve cascade order between an allowed shorthand and its longhands.
    properties.delete(property);
    properties.set(property, value);
  }
  return [...properties].map(([property, value]) => `${property}:${value}`).join(";");
}


const ALLOWED = new Set(["P", "BR", "STRONG", "EM", "A", "UL", "OL", "LI", "TABLE", "TBODY", "TR", "TD", "TH", "DIV", "SPAN", "HR", "H1", "H2", "H3", "H4", "H5", "H6", "IMG"]);
const DROP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "SVG", "MATH", "IFRAME", "OBJECT", "EMBED", "VIDEO", "AUDIO", "SOURCE", "PICTURE", "CANVAS", "FORM", "INPUT", "BUTTON", "TEXTAREA", "SELECT", "OPTION", "META", "LINK", "BASE", "HEAD", "TITLE"]);

export function signatureEditorFragment(source: string, contentId: string | null): DocumentFragment {
  if (source.length > 20_000) throw new Error("SIGNATURE_TOO_LARGE");
  // Template contents stay inert while hostile image/srcset/style attributes are removed.
  const template = document.createElement("template");
  template.innerHTML = source;
  const visit = (parent: ParentNode, depth = 0) => {
    if (depth > 64) throw new Error("SIGNATURE_TOO_COMPLEX");
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) { child.remove(); continue; }
      if (!(child instanceof HTMLElement)) {
        if (child.nodeType !== Node.TEXT_NODE) child.remove();
        continue;
      }
      if (DROP.has(child.tagName)) { child.remove(); continue; }
      if (child.tagName === "B" || child.tagName === "I") {
        const replacement = document.createElement(child.tagName === "B" ? "strong" : "em");
        replacement.append(...Array.from(child.childNodes));
        child.replaceWith(replacement);
        visit(replacement, depth + 1);
        continue;
      }
      if (!ALLOWED.has(child.tagName)) {
        visit(child, depth + 1);
        child.replaceWith(...Array.from(child.childNodes));
        continue;
      }
      const attrs: Record<string, string> = {};
      if (child.tagName === "A") {
        try {
          const url = new URL(child.getAttribute("href") ?? "");
          if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error();
          attrs.href = url.href;
          attrs.target = "_blank";
          attrs.rel = "noopener noreferrer";
        } catch {
          visit(child, depth + 1);
          child.replaceWith(...Array.from(child.childNodes));
          continue;
        }
      }
      if (child.tagName === "IMG") {
        if (!contentId || child.getAttribute("src") !== `cid:${contentId}`) { child.remove(); continue; }
        attrs.src = `cid:${contentId}`;
        attrs.alt = (child.getAttribute("alt") ?? "").slice(0, 250);
      }
      const integerNames = child.tagName === "IMG" ? ["width", "height"] : ["TD", "TH"].includes(child.tagName) ? ["colspan", "rowspan"] : [];
      for (const name of integerNames) {
        const value = child.getAttribute(name) ?? "";
        if (/^[1-9]\d{0,3}$/.test(value) && Number(value) <= (child.tagName === "IMG" ? 1200 : 12)) attrs[name] = String(Number(value));
      }
      const style = signatureEditorStyle(child.getAttribute("style") ?? "");
      if (style) attrs.style = style;
      for (const attr of Array.from(child.attributes)) child.removeAttribute(attr.name);
      for (const [key, value] of Object.entries(attrs)) child.setAttribute(key, value);
      visit(child, depth + 1);
    }
  };
  visit(template.content);
  return template.content;
}

export function normalizeSignatureEditorHtml(source: string, contentId: string | null): string {
  const template = document.createElement("template");
  template.content.append(signatureEditorFragment(source, contentId));
  const html = template.innerHTML.trim();
  if (html.length > 20_000) throw new Error("SIGNATURE_TOO_LARGE");
  return !signatureEditorPlainText(html, contentId) && !/<(?:img|hr|table)\b/.test(html) ? "" : html;
}

export function signatureEditorPlainText(source: string, contentId: string | null): string {
  const fragment = signatureEditorFragment(source, contentId);
  const text = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(/\s+/g, " ");
    if (!(node instanceof HTMLElement)) return "";
    if (node.tagName === "BR") return "\n";
    let value = Array.from(node.childNodes).map(text).join("");
    if (node.tagName === "A") {
      const href = node.getAttribute("href") ?? "";
      if (href && value.trim() !== href && value.trim() !== href.replace(/\/$/, "")) value += ` (${href})`;
    }
    if (["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6"].includes(node.tagName)) value += "\n\n";
    else if (["TD", "TH", "TR", "LI"].includes(node.tagName)) value += "\n";
    return value;
  };
  return Array.from(fragment.childNodes).map(text).join("").replace(/\n{3,}/g, "\n\n").trim();
}
